// src/components/VideoPlayer.jsx
import React, { useEffect, useRef, forwardRef, useState } from "react";
import Hls from "hls.js";

const HLS_CONFIG_SMOOTH = {
  // Smooth > low latency (voor sport veel fijner)
  lowLatencyMode: false,
  liveSyncDuration: 6,
  liveMaxLatencyDuration: 12,
  startPosition: -1,
  backBufferLength: 30,
  enableWorker: true,

  // extra buffer voor rustiger playback
  maxBufferLength: 30,
  maxMaxBufferLength: 60,
};

const HLS_CONFIG_LL = {
  lowLatencyMode: true,
  liveSyncDuration: 2.5,
  liveMaxLatencyDuration: 5.0,
  startPosition: -1,
  backBufferLength: 30,
  enableWorker: true,
};

function pickHlsConfig(preset) {
  if (preset === "ll") return HLS_CONFIG_LL;
  return HLS_CONFIG_SMOOTH; // default
}


// Onthoud of deze gebruiker audio al eens heeft geactiveerd op dit origin
const AUDIO_FLAG_KEY = "bond:audioAllowed";
const getAudioAllowed = () => {
  try {
    return localStorage.getItem(AUDIO_FLAG_KEY) === "1";
  } catch {
    return false;
  }
};
const setAudioAllowed = (v) => {
  try {
    localStorage.setItem(AUDIO_FLAG_KEY, v ? "1" : "0");
  } catch {}
};

const VideoPlayer = forwardRef(function VideoPlayer(
  {
    source,
    serverOffsetMs = 0,
    profile,
      hlsPreset = "smooth",

    mediaStateRef,
    needInitialAlignRef,
    didInitialAlignRef,
    lastFragBoostUntilRef,
    hardSeeksRef,
        onLiveMetrics,     // ✅ nieuw: callback met liveEdge/buffer info
    onSafeSeekRef,     // ✅ nieuw: geeft App.js een safeSeek() functie

    style,
    onDoubleClick,
    controlsList = "nodownload noplaybackrate noremoteplayback",
    playsInline = true,
    preload = "auto",
    objectFit = "contain",
  },
  videoRef
) {
  const hlsInstRef = useRef(null);

  function getBufferedRange(v) {
  try {
    const t = v.currentTime;
    for (let i = 0; i < v.buffered.length; i++) {
      const start = v.buffered.start(i);
      const end = v.buffered.end(i);
      if (t >= start && t <= end) return { start, end };
    }
    // fallback: neem laatste range als currentTime net erbuiten ligt
    if (v.buffered.length > 0) {
      const end = v.buffered.end(v.buffered.length - 1);
      const start = v.buffered.start(v.buffered.length - 1);
      return { start, end };
    }
  } catch {}
  return { start: null, end: null };
}

function getLiveEdge(hls) {
  try {
    // hls.liveSyncPosition is vaak de beste “safe live target”
    const p = hls?.liveSyncPosition;
    return typeof p === "number" && Number.isFinite(p) ? p : null;
  } catch {
    return null;
  }
}


  const serverOffsetRef = useRef(serverOffsetMs);
useEffect(() => {
  serverOffsetRef.current = serverOffsetMs;
}, [serverOffsetMs]);

const boostWindowRef = useRef(profile?.BOOST_WINDOW_MS);
useEffect(() => {
  boostWindowRef.current = profile?.BOOST_WINDOW_MS;
}, [profile?.BOOST_WINDOW_MS]);


  // deze states gebruiken we nog intern voor autoplay-policy, maar we tonen géén eigen UI meer
  const [muted, setMuted] = useState(true); // autoplay policy: start muted
  const [volume] = useState(1); // 0..1
  const [uiVisible, setUiVisible] = useState(true); // heeft nu geen visueel effect meer
  const audioAllowedRef = useRef(getAudioAllowed());

  // HLS attach + initial align
 // HLS attach + initial align

// 1) low-level seek (no clamps)
const seekRawRef = useRef((t) => {
  const v = videoRef?.current;
  if (!v || !Number.isFinite(t)) return;
  try {
    v.fastSeek ? v.fastSeek(t) : (v.currentTime = t);
  } catch {}
});

// 2) safeSeek = clamp for HLS live/buffer window, then call seekRaw
const safeSeekRef = useRef((t) => {
  const v = videoRef?.current;
  if (!v || !Number.isFinite(t)) return;

  const hls = hlsInstRef.current;

  // Als we HLS hebben: clamp naar liveEdge en buffer-window
  if (hls && source?.mode === "hls") {
    const liveEdge = getLiveEdge(hls);
    const { start, end } = getBufferedRange(v);

    let target = t;

    // 1) Niet voorbij live edge
    if (liveEdge != null && target > liveEdge) target = liveEdge;

    // 2) Niet te ver terug buiten buffer (kan bij live window/playlist shifts)
    if (start != null && target < start) {
      // Als liveEdge bekend is: snap daarheen, anders naar buffer end
      target = liveEdge != null ? liveEdge : end != null ? end : target;
    }

    seekRawRef.current(target);
    return;
  }

  // MP4 / native: gewone seek
  seekRawRef.current(t);
});





  useEffect(() => {
    const v = videoRef?.current;

    // ✅ Expose safeSeek naar parent (App.js)
if (typeof onSafeSeekRef === "function") {
  onSafeSeekRef((t) => safeSeekRef.current(t));
}

    if (!v || !source) return;

    // teardown
    if (hlsInstRef.current) {
      try {
        hlsInstRef.current.destroy();
      } catch {}
      hlsInstRef.current = null;
    }

    // basis video props
    v.playsInline = playsInline;
    v.preload = preload;
    v.volume = volume;
    v.muted = true; // BELANGRIJK: autoplay toestaan

    if (source.mode === "hls") {
      if (Hls.isSupported()) {
const hls = new Hls(pickHlsConfig(hlsPreset));
        hlsInstRef.current = hls;
        hls.attachMedia(v);
        hls.on(Hls.Events.MEDIA_ATTACHED, () => {
          hls.loadSource(source.url);
        });

        const tryInitialSnap = () => {
          const s = mediaStateRef?.current;
          const now = Date.now() + serverOffsetRef.current;
          if (s) {
            const elapsed = Math.max(0, (now - s.serverEpoch) / 1000);
            const target = s.playing ? s.mediaTime + elapsed : s.mediaTime;
            try {
  safeSeekRef.current(target);
} catch {}


          } else {
            try {
              hls.startLoad(-1);
            } catch {}
          }
          // probeer meteen te spelen (muted → toegestaan)
          v.play().catch(() => {});
        };

       hls.on(Hls.Events.MANIFEST_PARSED, () => {
  if (hls.audioTracks && hls.audioTracks.length > 0) {
    hls.audioTrack = 0; // kies eerste track
  }

  // initial align
  if (needInitialAlignRef?.current) {
    tryInitialSnap();

    const onPlaying = () => {
      needInitialAlignRef.current = false;
      didInitialAlignRef && (didInitialAlignRef.current = true);
      v.removeEventListener("playing", onPlaying);
    };

    v.addEventListener("playing", onPlaying);

    // safety: als playing niet snel komt, listener opruimen (watchdog neemt het daarna over)
    setTimeout(() => {
      try {
        v.removeEventListener("playing", onPlaying);
      } catch {}
    }, 3000);
  }
});


        hls.on(Hls.Events.LEVEL_LOADED, (_e, data) => {
          if (
            data?.details?.live &&
            lastFragBoostUntilRef &&
boostWindowRef.current
          ) {
            lastFragBoostUntilRef.current = Date.now() + boostWindowRef.current;

          }
        });

        hls.on(Hls.Events.FRAG_LOADED, () => {
          if (lastFragBoostUntilRef && boostWindowRef.current) {
  lastFragBoostUntilRef.current =
    Date.now() + boostWindowRef.current;
}

          // ✅ live metrics (buffer + liveEdge) voor App.js guardrails / debug
          if (typeof onLiveMetrics === "function") {
            const liveEdge = getLiveEdge(hls);
            const { start, end } = getBufferedRange(v);
            onLiveMetrics({
              liveEdge,
              bufStart: start,
              bufEnd: end,
              now: Date.now(),
            });
          }


        });

       hls.on(Hls.Events.ERROR, (_event, data) => {
  if (!data) return;

  // Niet-fataal: meestal niets doen. Bij stall proberen we alleen play() te herstellen.
  if (!data.fatal) {
    if (data.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR) {
      v.play().catch(() => {});
    }
    return;
  }

  console.warn("[HLS fatal]", data.type, data.details);

  // Fataal: herstel per type (Hls.js recommended recovery)
  if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
    try {
      hls.recoverMediaError();
    } catch {}
    return;
  }

  if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
    try {
      hls.startLoad();
    } catch {}
    return;
  }

  // Overig fataal: volledige reset van Hls instance
  try {
    const url = source.url;

    // huidige kapotte instance weg
    hls.destroy();
    hlsInstRef.current = null;

    // nieuwe instance
const h2 = new Hls(pickHlsConfig(hlsPreset));
    hlsInstRef.current = h2;
    h2.attachMedia(v);
    h2.on(Hls.Events.MEDIA_ATTACHED, () => {
      h2.loadSource(url);
    });
  } catch {}
});


      } else {
        // Safari native HLS
        v.src = source.url || "";
        v.play().catch(() => {}); // muted autoplay
      }
    } else {
      // MP4
      v.src = source.url || "";
      v.play().catch(() => {}); // muted autoplay
    }

    const onCanPlay = () => {
      // fallback align voor MP4/native HLS
      if (needInitialAlignRef?.current) {
        const s = mediaStateRef?.current;
        const now = Date.now() + serverOffsetRef.current;
        if (s) {
          const elapsed = Math.max(0, (now - s.serverEpoch) / 1000);
          const target = s.playing ? s.mediaTime + elapsed : s.mediaTime;
          try {
            v.fastSeek ? v.fastSeek(target) : (v.currentTime = target);
          } catch {}
        }
        needInitialAlignRef.current = false;
        didInitialAlignRef && (didInitialAlignRef.current = true);
      }

      // Als user dit origin eerder toestemming gaf, zet meteen unmute
      if (audioAllowedRef.current) {
        v.muted = false;
        setMuted(false);
      }
    };

    v.addEventListener("canplay", onCanPlay);
    return () => {
      v.removeEventListener("canplay", onCanPlay);
      if (hlsInstRef.current) {
        try {
          hlsInstRef.current.destroy();
        } catch {}
        hlsInstRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, [source?.mode, source?.url, hlsPreset]);

// ✅ Local play watchdog (anti-freeze): houdt playback levend als paused blijft hangen
useEffect(() => {
  const v = videoRef?.current;
  if (!v) return;

  const id = setInterval(() => {
    // als we genoeg data hebben en hij blijft paused: probeer play
    if (v.paused && !v.ended && v.readyState >= 2) {
      v.play().catch(() => {});
    }
  }, 400);

  return () => clearInterval(id);
}, [source?.mode, source?.url]);


// ✅ Tab-switch / background resilience (player-layer)
// ✅ Tab-switch resilience (player-layer) — ONLY restart loading/play, no seeks
useEffect(() => {
  const v = videoRef?.current;
  if (!v) return;

  const resume = async () => {
    const mode = source?.mode;
    const hls = hlsInstRef.current;

    // HLS: resume loading if suspended
    if (hls && mode === "hls") {
      try { hls.startLoad(); } catch {}
    }

    // Try to play (some browsers block)
    try {
      if (v.paused) await v.play();
    } catch {}
  };

  const onVis = () => {
    if (document.visibilityState === "visible") resume();
  };

  document.addEventListener("visibilitychange", onVis);

  return () => {
    document.removeEventListener("visibilitychange", onVis);
  };
}, [source?.mode, source?.url]);



  // kleine auto-hide state blijft bestaan, maar we tonen geen extra UI meer
  useEffect(() => {
    if (!uiVisible) return;
    const id = setTimeout(() => setUiVisible(false), 2500);
    return () => clearTimeout(id);
  }, [uiVisible]);

  return (
    <div
      onMouseMove={() => setUiVisible(true)}
      style={{ position: "relative", width: "100%", height: "100%" }}
    >
      <video
        ref={videoRef}
        onDoubleClick={onDoubleClick}
        controls={false}
        controlsList={controlsList}
        style={{
          width: "100%",
          height: "100%",
          objectFit,
          background: "#000",
          ...(style || {}),
        }}
      />
      {/* Geen eigen volume/mute UI meer hier:
          de audiomixer zit nu in App.js linksonder in de video-area */}
    </div>
  );
});

export default VideoPlayer;
